import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Calculator, RefreshCw, FileText, Copy, ArrowRight } from "lucide-react";
import AdvancedTable from "@/components/table/AdvancedTable";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { InlineText, InlinePicker, CommentsPopover,
         ActivityPopover } from "@/components/list-inline/InlineRow";
import LinkedBadges from "@/pages/app/gst-sales/LinkedBadges";
import LinkedPreviewDialog from "@/pages/app/gst-sales/LinkedPreviewDialog";
import DetailDialog from "@/pages/app/gst-sales/DetailDialog";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  in_review: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
  converted: "bg-primary/10 text-primary border-primary/30",
  cancelled: "bg-rose-100 text-rose-700 border-rose-300",
};

function fmtMoney(v, currency = "INR") {
  if (v === null || v === undefined) return "—";
  return `${currency === "INR" ? "₹" : currency + " "}${Number(v).toLocaleString("en-IN")}`;
}

export default function EstimateList() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showTemplates, setShowTemplates] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  // Feb 2026 R293zze · Preload chain for every visible estimate so the
  // Linked column shows real badges on first paint.
  const [chainById, setChainById] = useState({});
  // Feb 2026 R293zze · In-place preview + GST doc dialog for Linked
  // column click-through (no new tab).
  const [linkedPreview, setLinkedPreview] = useState(null); // { kind, id }
  const [gstDetailDoc, setGstDetailDoc] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (clientFilter) params.client_id = clientFilter;
      if (brandFilter) params.brand_id = brandFilter;
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/estimates`, { params });
      setRows(data || []);
      const ids = (data || []).map((r) => r.id).filter(Boolean);
      if (ids.length) {
        try {
          const cr = await api.post(
            `/gst/workspaces/${activeWorkspaceId}/estimates/chain-bulk`,
            { ids });
          setChainById(cr.data || {});
        } catch { setChainById({}); }
      } else {
        setChainById({});
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [activeWorkspaceId, filter, clientFilter, brandFilter]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/clients`).then(r => r.data || []).catch(() => []),
      api.get(`/workspaces/${activeWorkspaceId}/brands`).then(r => r.data || []).catch(() => []),
    ]).then(([cs, bs]) => { setClients(cs); setBrands(bs); });
  }, [activeWorkspaceId]);

  const patchRow = async (eid, patch) => {
    try {
      await api.patch(`/estimates/${eid}`, patch);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const duplicate = async (eid) => {
    try {
      const { data } = await api.post(`/estimates/${eid}/duplicate`);
      toast.success(`Duplicated as ${data.number}`);
      load();
    } catch { toast.error("Duplicate failed"); }
  };

  // Feb 2026 R293zze · One-click: push the estimate's items into a
  // fresh GST Sales Quotation (QUO-). Reuses the existing push
  // endpoint which stamps `source_estimate_id` for round-tripping.
  const pushToGstQuotation = async (eid) => {
    try {
      const { data } = await api.post(
        `/gst/workspaces/${activeWorkspaceId}/standalone-estimates/${eid}/to-gst-quotation`,
        {});
      toast.success(data?.reused
        ? `Opened existing quotation ${data.quotation?.doc_no}`
        : `Created quotation ${data.quotation?.doc_no}`);
      // Deep-link into the Quotations tab opened to the fresh doc
      nav(`/app/sales?t=estimate&open=${data.quotation?.id || ""}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create quotation")); }
  };

  const clientOpts = [{ value: "", label: "— No client —" },
    ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.client_code }))];
  const brandOpts = (cid) => [{ value: "", label: "— No brand —" },
    ...brands.filter((b) => !cid || b.client_id === cid)
             .map((b) => ({ value: b.id, label: b.name }))];

  return (
    <div className="p-6 space-y-5" data-testid="estimate-list-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <Calculator className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Estimates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Build detailed estimates with line-item rate picking and sub-item effort breakdown.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/estimates/variance" className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                 data-testid="estimate-list-variance-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Variance
          </Link>
          <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button type="button" onClick={() => setShowTemplates(true)}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                   data-testid="estimate-list-templates-btn">
            <FileText className="w-3.5 h-3.5" /> Templates
          </button>
          <Link to="/app/rfqs" className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium">
            <Plus className="w-3.5 h-3.5" /> Start from RFQ
          </Link>
        </div>
      </div>

      {showTemplates && (
        <TemplatesDialog wsId={activeWorkspaceId}
          onClose={() => setShowTemplates(false)}
          onPicked={async (tid) => {
            try {
              const { data } = await api.post(
                `/workspaces/${activeWorkspaceId}/estimates`,
                { title: "New estimate from template",
                  from_template_id: tid });
              toast.success(`Created ${data.number}`);
              setShowTemplates(false);
              nav(`/app/estimates/${data.id}`);
            } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
          }} />
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="estimate-filters">
        <div className="w-52">
          <InlinePicker value={clientFilter}
                         display={clientFilter ? (clients.find(c => c.id === clientFilter)?.name || "Client") : "All clients"}
                         options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]}
                         onSave={setClientFilter}
                         testid="estimate-filter-client" />
        </div>
        <div className="w-52">
          <InlinePicker value={brandFilter}
                         display={brandFilter ? (brands.find(b => b.id === brandFilter)?.name || "Brand") : "All brands"}
                         options={[{ value: "", label: "All brands" }, ...brands.filter((b) => !clientFilter || b.client_id === clientFilter).map((b) => ({ value: b.id, label: b.name }))]}
                         onSave={setBrandFilter}
                         testid="estimate-filter-brand" />
        </div>
        {(clientFilter || brandFilter) && (
          <button type="button" onClick={() => { setClientFilter(""); setBrandFilter(""); }}
                   className="text-[10px] px-2 py-1.5 border border-border hover:bg-muted text-muted-foreground"
                   data-testid="estimate-clear-filters">
            Clear filters
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["all", "draft", "in_review", "approved", "converted", "rejected"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-1.5 border transition ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`estimate-filter-${s}`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {/* Iter 245 · Advanced table */}
      <AdvancedTable
        tableKey="estimates"
        title="Estimates"
        rows={rows}
        loading={loading}
        emptyTitle="No estimates yet"
        emptyDescription="Create an RFQ first, then build an estimate from it."
        onRowClick={(r) => nav(`/app/estimates/${r.id}`)}
        columns={[
          { key: "number", label: "Number", width: 110, sortable: true, filterType: "text", frozen: true,
            render: (r) => (
              <Link to={`/app/estimates/${r.id}`} onClick={(e) => e.stopPropagation()}
                    className="font-mono text-[11px] text-primary hover:underline">{r.number}</Link>
            ) },
          { key: "title", label: "Title", width: 260, sortable: true, filterType: "text",
            render: (r) => (
              <div onClick={(e) => e.stopPropagation()}>
                <InlineText value={r.title || ""} onSave={(v) => patchRow(r.id, { title: v })}
                             locked={r.status === "approved" || r.status === "converted"}
                             testid={`estimate-title-${r.id}`} />
              </div>
            ) },
          { key: "client_name", label: "Client", width: 160, sortable: true, filterType: "text",
            render: (r) => (
              <div onClick={(e) => e.stopPropagation()}>
                <InlinePicker value={r.client_id || ""}
                              display={r.client_name || <span className="opacity-50">—</span>}
                              options={clientOpts}
                              onSave={(v) => patchRow(r.id, { client_id: v || null, brand_id: null })}
                              locked={r.status === "approved" || r.status === "converted"}
                              testid={`estimate-client-${r.id}`} />
              </div>
            ) },
          { key: "brand_name", label: "Brand", width: 140, sortable: true, filterType: "text",
            render: (r) => (
              <div onClick={(e) => e.stopPropagation()}>
                <InlinePicker value={r.brand_id || ""}
                              display={r.brand_name || <span className="opacity-50">—</span>}
                              options={brandOpts(r.client_id)}
                              onSave={(v) => patchRow(r.id, { brand_id: v || null })}
                              locked={r.status === "approved" || r.status === "converted"}
                              testid={`estimate-brand-${r.id}`} />
              </div>
            ) },
          { key: "status", label: "Status", width: 120, sortable: true, filterType: "select",
            filterOptions: ["draft","in_review","approved","converted","rejected"].map((v) => ({ value: v, label: v.replace(/_/g," ") })),
            render: (r) => (
              <span className={`inline-block px-2 py-0.5 border text-[10px] ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>
                {r.status?.replace("_", " ")}
              </span>
            ) },
          { key: "grand_total", label: "Grand total", width: 130, sortable: true, align: "right", type: "number",
            render: (r) => (
              <span className="font-mono text-xs">{r._rates_hidden ? "•••" : fmtMoney(r.grand_total, r.currency)}</span>
            ) },
          { key: "updated_at", label: "Updated", width: 110, sortable: true, filterType: "date-range",
            render: (r) => <span className="text-[11px] text-muted-foreground">{fmtRelative(r.updated_at || r.created_at)}</span> },
          { key: "linked", label: "Linked", width: 140,
            render: (r) => (
              <div onClick={(e) => e.stopPropagation()}>
                <LinkedBadges
                  ws={activeWorkspaceId}
                  docId={r.id}
                  source="estimate"
                  preloaded={chainById[r.id] || null}
                  onOpen={async (hit) => {
                    if (hit.kind === "rfqs") {
                      setLinkedPreview({ kind: "rfq", id: hit.id });
                      return;
                    }
                    if (hit.kind === "estimates") {
                      setLinkedPreview({ kind: "estimate", id: hit.id });
                      return;
                    }
                    // GST doc kind — fetch and open DetailDialog in-place
                    try {
                      const d = await api.get(`/gst/workspaces/${activeWorkspaceId}/documents/${hit.id}`);
                      setGstDetailDoc(d.data);
                    } catch {
                      const typeToTab = { quotations: "estimate", sales_orders: "sales_order", invoices: "invoice", credit_notes: "credit_note" };
                      const tab = typeToTab[hit.kind] || "invoice";
                      nav(`/app/sales?t=${tab}&open=${hit.id}`);
                    }
                  }}
                  compact
                />
              </div>
            ) },
          { key: "actions", label: "", width: 170,
            render: (r) => (
              <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-0.5 justify-end">
                <CommentsPopover docType="estimate" docId={r.id} count={r.comments_count || 0} />
                <ActivityPopover docType="estimate" docId={r.id} />
                <button type="button" onClick={() => pushToGstQuotation(r.id)}
                         className="text-[10px] px-1.5 py-1 border border-border hover:bg-muted flex items-center gap-1 text-violet-700 dark:text-violet-300"
                         title="Create Quotation — pushes these line items into a fresh GST Sales Quotation"
                         data-testid={`estimate-to-quotation-${r.id}`}>
                  <ArrowRight className="w-3 h-3" /> Quotation
                </button>
                <button type="button" onClick={() => duplicate(r.id)}
                         className="text-[10px] px-1 py-1 text-muted-foreground hover:text-foreground"
                         title="Duplicate" data-testid={`estimate-duplicate-${r.id}`}>
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            ) },
        ]}
      />

      <LinkedPreviewDialog
        open={!!linkedPreview}
        ws={activeWorkspaceId}
        kind={linkedPreview?.kind}
        docId={linkedPreview?.id}
        onClose={() => setLinkedPreview(null)}
      />
      <DetailDialog
        doc={gstDetailDoc}
        ws={activeWorkspaceId}
        onClose={() => setGstDetailDoc(null)}
      />
    </div>
  );
}


// ────────────────────── Iter 242 · Templates picker ──────────────────────
function TemplatesDialog({ wsId, onClose, onPicked }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/estimate-templates`)
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, [wsId]);
  const remove = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/estimate-templates/${tid}`);
      setRows((prev) => prev.filter((r) => r.id !== tid));
    } catch { toast.error("Delete failed"); }
  };
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-background border border-border shadow-2xl" data-testid="estimate-templates-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Pick an estimate template</div>
        <div className="p-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="text-xs text-muted-foreground p-6 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-muted-foreground p-6 text-center border border-dashed border-border">
              No templates yet. From any estimate, click <b>Save as template</b> to create one.
            </div>
          ) : (
            <div className="divide-y divide-border border border-border">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-3 hover:bg-muted/30" data-testid={`tpl-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {r.source_estimate_number || "—"} · {r.line_count} line{r.line_count === 1 ? "" : "s"} · {r.sub_item_count} effort row{r.sub_item_count === 1 ? "" : "s"}
                    </div>
                    {r.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{r.description}</div>}
                  </div>
                  <button type="button" onClick={() => onPicked(r.id)}
                           className="text-xs px-3 py-1.5 bg-primary text-primary-foreground font-medium"
                           data-testid={`tpl-use-${r.id}`}>
                    Use
                  </button>
                  <button type="button" onClick={() => remove(r.id)}
                           className="text-xs text-muted-foreground hover:text-rose-500 px-2 py-1.5"
                           data-testid={`tpl-delete-${r.id}`}>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Close</button>
        </div>
      </div>
    </div>
  );
}
